import { store } from '../../../store'
import { MessageDataType } from '../types'
import { SpotOrderRecord } from '../../../api/v1/spot'
import { PerpOrderRecord } from '../../../api/v1/perp'
import { OrderId, UserId } from '../../../types'
import toastify from '../../../components/Toastify'
import { getSpotOpenOrdersAction } from '../../../store/spot/actions/getSpotOpenOrdersActions'
import {
  updateSpotCurrentOrderTab,
  updateSpotOrderRecordAction,
} from '../../../store/spot/actions'
import { getPerpOpenOrdersAction } from '../../../store/perp/actions/getPerpOpenOrdersActions'
import {
  updateClosingPositionStatusAction,
  updatePerpCurrentOrderTab,
  updatePerpOrderRecordAction,
} from '../../../store/perp/actions'
import { getPerpPositionsAction } from '../../../store/perp/actions/getPerpPositionsActions'
import { OrderManager } from '../../../utils/orderManager'
import { ORDER_FAIL_REASONS } from '../../../constants/trade'
import { PerpOrderTabType } from '../../../store/perp/types'
import { SpotOrderTabType } from '../../../store/spot/types'

export interface WsOrderMessage<T> {
  type:
    | 'order_filled'
    | 'order_pending'
    | 'order_cancelled'
    | 'order_failed'
    | 'order_matched'
    | 'position_changed'
    | 'stop_order_triggered'
  data: T
  sequenceId: number
  userId: UserId
}

// Websocket message has different data structure for spot and perp, so we need to merge them
// 'tradeType' can be used to distinguish spot and perp
export type WsOrderRecord = (SpotOrderRecord | PerpOrderRecord) & {
  orderId: OrderId
  tradeType: 'SPOTS' | 'CONTRACTS' | 'INCREASE' | 'DECREASE' | 'CLOSE'
}

export function onMessageOrder(data: MessageDataType) {
  switch (data.type) {
    // Receive new order and finished order data,
    // used to display in the 'UserOrders/OpenOrder and UserOrders/OrderHistory' component
    case 'order_pending':
    case 'order_cancelled':
    case 'order_failed':
    case 'order_filled':
    case 'stop_order_triggered':
      updateOrder(data as WsOrderMessage<WsOrderRecord>)
      break
  }

  switch (data.type) {
    case 'order_filled':
    case 'order_failed':
    case 'order_cancelled':
      showToast(data as WsOrderMessage<WsOrderRecord>)
      break
  }

  switch (data.type) {
    case 'order_pending':
    case 'position_changed':
      activeOrderTab(data as WsOrderMessage<WsOrderRecord>)
      break
  }
}

function activeOrderTab(data: WsOrderMessage<WsOrderRecord>) {
  const { tradeType } = data.data

  switch (tradeType) {
    case 'INCREASE':
    case 'DECREASE':
      store.dispatch(updatePerpCurrentOrderTab(PerpOrderTabType.Position))
      break
    case 'SPOTS':
      store.dispatch(updateSpotCurrentOrderTab(SpotOrderTabType.OpenOrder))
      break
    case 'CONTRACTS':
      store.dispatch(updatePerpCurrentOrderTab(PerpOrderTabType.OpenOrder))
      break
  }
}

function showToast(
  data: WsOrderMessage<
    WsOrderRecord & {
      failReason?: string
    }
  >,
) {
  const { type = '' } = data
  const { id = 0, status = '' } = data.data

  const toastId = id + status

  if (type === 'order_filled') {
    if (status === 'FULLY_FILLED') {
      toastify.success('Order Fully Filled', {
        id: toastId,
      })
    }
  } else if (type === 'order_cancelled') {
    if (status === 'FULLY_CANCELLED') {
      toastify.warn('Order Fully Cancelled', {
        id: toastId,
      })
    }
  }
  // Show toastify message when order failed
  else if (type === 'order_failed') {
    const { clientOrderId, failReason = '' } = data.data

    if (failReason) {
      let message =
        ORDER_FAIL_REASONS[failReason as keyof typeof ORDER_FAIL_REASONS] ||
        failReason

      if (OrderManager.isCloseOrder(clientOrderId)) {
        if (failReason === 'ACCOUNT_NO_ENOUGH_AVAILABLE') {
          message =
            ORDER_FAIL_REASONS.ACCOUNT_NO_ENOUGH_AVAILABLE_FROZEN || failReason
        }
      }

      toastify.error(message, {
        id: toastId,
      })
    }
  }
}

function updateOrder(data: WsOrderMessage<WsOrderRecord>) {
  const { orderId, ...d } = data.data
  // The data of close position orders and user placed orders are inconsistent.
  // Copy the 'orderId' field from the close position order and assign it as an 'id' to the user placed order.
  const payload: WsOrderMessage<SpotOrderRecord | PerpOrderRecord> = {
    ...data,
    data: {
      ...d,
      id: orderId,
    },
  }
  const state = store.getState()
  const { accountInfo } = state.account

  if (d.tradeType === 'SPOTS') {
    store.dispatch(getSpotOpenOrdersAction(accountInfo?.accountId as number)) // Refresh the open order list
    store.dispatch(
      updateSpotOrderRecordAction(payload as WsOrderMessage<SpotOrderRecord>),
    ) // Update the order record
  } else if (d.tradeType === 'CONTRACTS') {
    store.dispatch(getPerpOpenOrdersAction(accountInfo?.accountId as number)) // Refresh the open order list
    store.dispatch(
      updatePerpOrderRecordAction(payload as WsOrderMessage<PerpOrderRecord>),
    ) // Update the order record
    if (data.type !== 'order_failed') {
      store.dispatch(getPerpPositionsAction()) // Refresh the position list
    }

    // When a close position fails and the closing operation is canceled
    // The status of the close position needs to be terminated.
    if (
      data.type === 'order_cancelled' ||
      data.type === 'order_failed' ||
      data.type === 'order_filled'
    ) {
      tryUpdateClosingPositionStatus(payload)
    }
  }

  // OrderEmitter.publish(payload.data.id, payload.data as OrderEmitterData)
}

// Define a function to check if the ID is in the closePositionOrderIds array
// and dispatch the action if it is.
function tryUpdateClosingPositionStatus(
  payload: WsOrderMessage<SpotOrderRecord | PerpOrderRecord>,
) {
  const checkAndDispatch = () => {
    const state = store.getState()
    const { closePositionOrderIds } = state.perp

    if (closePositionOrderIds.includes(Number(payload.data.id))) {
      store.dispatch(
        updateClosingPositionStatusAction(
          payload as WsOrderMessage<PerpOrderRecord>,
        ),
      )
      return true // Return true indicating the ID was found and action dispatched
    }
    return false // Return false indicating the ID was not found
  }

  // Initial check: If the ID is not found in the current state, set up a retry mechanism
  if (!checkAndDispatch()) {
    let retry = 0

    // Because the close position order is asynchronous,
    // the 'addClosePositionOrderIds' action always dispatches after this 'order_cancelled' message.
    // Using the setInterval function to check the state every 3 seconds.
    const timer = setInterval(() => {
      const success = checkAndDispatch() // Check the state and dispatch the action
      retry += 1

      // Clear the interval if the action was dispatched successfully or if the maximum number of retries has been reached
      if (success || retry >= 3) {
        clearInterval(timer)
      }
    }, 2000) // Set the interval to 2000 milliseconds (2 seconds)
  }
}
